// Builds the TRF fed to bbpPairings to pair TEAMS against each other.
//
// The trick, and the whole reason this file is short: a team Swiss is an ordinary Swiss
// played by N "super-players" who score match points. So each TEAM is written as one 001
// player line — its pairing number is the Team Pairing Number, its score is its match
// points, its opponents are the teams it has met, and its colour is the colour its board 1
// held. bbpPairings then does the bracket, float and bye work it is already trusted for.
//
// This is not a way around the engine's refusal to read team files; it is the alternative
// to it. bbpPairings rejects a 013 record outright
// (vendor/bbpPairings/src/fileformats/trf.cpp:1093, "team tournaments are not supported"),
// and JaVaFo has no team mode at all, so a real team TRF cannot be handed to either. What
// they *can* pair is this projection of it.
//
// WHAT THE PROJECTION DELIBERATELY DROPS. Colours. The engine will apply the individual
// Dutch colour rules (Basic Rules art. 6 and 7) to whatever history it is given, and
// C.04.6 excludes both from team play. So the colours it returns are discarded and
// reassigned by TeamEngine.assignMatchColours per art. 4.3 — which is the rulebook's own
// sequencing, not a patch over it (art. 3.2 pairs first and colours last). The history
// written here is still the real one, because the engine needs it to know who has met whom
// and to keep its own bookkeeping consistent.
//
// See js/services/pairing-trf.js for the individual equivalent; the two are deliberately
// separate documents rather than one parameterised builder, because almost nothing about
// the player section survives the projection.

const TeamPairingTRF = {

    /**
     * Point values in the engine's model, for a field of teams.
     *
     * These are NOT bbpPairings' defaults, so they must always be declared in the file:
     * a match win is worth 2 rather than 1, and — the part that surprises people — a
     * pairing-allocated bye is worth a DRAW, not a win. C.04.6 art. 2.5 is explicit that a
     * team receiving the PAB gets draw-equivalent points in both scorings, whereas an
     * individual PAB is normally a full point.
     */
    pointValues(matchPointSystem) {
        const ps = (matchPointSystem || '2-1-0').split('-').map(Number);
        const win = Number.isFinite(ps[0]) ? ps[0] : 2;
        const draw = Number.isFinite(ps[1]) ? ps[1] : 1;
        const loss = Number.isFinite(ps[2]) ? ps[2] : 0;
        return { win, draw, loss, zeroPointBye: 0, forfeitLoss: 0, pab: draw };
    },

    /**
     * One round's cell for one team: { oppNo, color, result }.
     *
     * Forfeited matches are written as ordinary wins and losses ('1' / '0'), NOT as the
     * TRF forfeit codes '+' / '-'. That is deliberate and it matters: bbpPairings treats
     * '+' and '-' as games that were never played, and two players who never played may
     * be paired again. For individuals that is correct Dutch behaviour. For teams it is
     * not — C.04.6 [C1] says two teams may not play each other more than once, and a
     * match awarded by forfeit was still a pairing. Writing it as a played result is what
     * keeps the engine from pairing the same two teams twice.
     */
    cellFor(stat, roundNo, teamNoOf) {
        const r = (stat.results || []).find(x => x.roundNo === roundNo);
        if (!r) return null;

        if (r.opponentId === 'BYE') {
            return { oppNo: '0000', color: '-', result: 'U' };
        }

        const oppNo = teamNoOf(r.opponentId);
        const color = r.colour === 'W' ? 'w' : 'b';
        let result;
        if (r.gp > r.oppGp) result = '1';
        else if (r.gp < r.oppGp) result = '0';
        else result = '=';
        return { oppNo: String(oppNo), color, result };
    },

    /**
     * A team's 001 line. The player section's meaning is reused wholesale: starting rank
     * number is the TPN, "rating" is the team's strength factor (only ever a tie-break
     * inside the engine), and the name is the team's.
     */
    teamLine(team, stat, cells, score, rank) {
        const fields = [
            { col: 1, text: '001', width: 3 },
            { col: 5, text: parseInt(team.teamNo, 10) || 0, width: 4, align: 'right' },
            { col: 15, text: (team.name || '').substring(0, 33), width: 33 },
            { col: 49, text: parseInt(team.strength, 10) || '', width: 4, align: 'right' },
            { col: 54, text: team.fed || '', width: 3 },
            { col: 81, text: Number(score).toFixed(1), width: 4, align: 'right' },
            { col: 86, text: rank || '', width: 4, align: 'right' }
        ];
        cells.forEach((c, ri) => {
            if (!c) return;
            const base = 92 + ri * 10;
            fields.push({ col: base, text: c.oppNo, width: 4, align: 'right' });
            fields.push({ col: base + 5, text: c.color, width: 1 });
            fields.push({ col: base + 7, text: c.result, width: 1 });
        });
        return window.TRFExport.buildLine(fields);
    },

    /**
     * @param {Object} opts
     * @param {Array}  opts.teams   [{ id, teamNo, name, strength, status }] — `status` false
     *                              means "do not pair this team this round"
     * @param {Array}  opts.rounds  completed rounds, each { roundNo, matches, pairings }
     * @param {Object} opts.meta    { name, numRounds, initialColour, matchPointSystem,
     *                                pointSystem, boards, primaryScore }
     * @returns {{ trf: string, teamByNo: Map<number,string>, warnings: string[] }}
     * @throws {Error} with a message an arbiter can act on
     */
    build({ teams, rounds, meta }) {
        teams = teams || [];
        rounds = rounds || [];
        meta = meta || {};
        const NL = '\r\n';
        const warnings = [];

        // Every refusal below reaches the arbiter as a dialog — see TeamEngine.text.
        const say = (key, params) => window.TeamEngine.text(key, params);

        // ---- what the projection cannot express ------------------------------
        // A TRF round cell holds one of win / draw / loss. That is enough to reconstruct a
        // team's MATCH points but not its GAME points, which move in half-points across
        // several boards, so an event that pairs on game points cannot be projected onto
        // this file at all. Refusing loudly is the only honest option: pairing it on match
        // points instead would silently build different scoregroups from the ones the
        // competition's own rules define.
        if (meta.primaryScore === 'game') {
            throw new Error(say(
                'This tournament ranks teams by game (board) points, and the pairing engine can only be given win/draw/loss per round, so it cannot form scoregroups on game points. Rank by match points to pair automatically, run the event as a round robin (whose schedule needs no scores at all), or pair this round by hand.'
            ));
        }

        // ---- validation ------------------------------------------------------
        const seen = new Map();
        teams.forEach(t => {
            const n = parseInt(t.teamNo, 10);
            if (!Number.isFinite(n) || n <= 0) {
                throw new Error(say(
                    '"{name}" has no team pairing number. Assign pairing numbers to every team before generating pairings.',
                    { name: t.name || say('A team') }
                ));
            }
            if (seen.has(n)) {
                throw new Error(say(
                    'Team pairing number {n} is used by both "{first}" and "{second}". Pairing numbers must be unique.',
                    { n, first: seen.get(n), second: t.name }
                ));
            }
            seen.set(n, t.name);
        });

        const totalRounds = parseInt(meta.numRounds, 10) || rounds.length + 1;
        if (rounds.length >= totalRounds) {
            throw new Error(say('All {n} rounds have already been played.', { n: totalRounds }));
        }

        // Every match must be finished, or a team's match points — and therefore its
        // scoregroup — are not yet known.
        rounds.forEach(round => {
            (round.matches || []).forEach(m => {
                if (m.blackTeam === 'BYE') return;
                const boards = (round.pairings || []).filter(p => p.matchId === m.id);
                if (boards.length === 0 || boards.some(p => !p.result)) {
                    throw new Error(say(
                        'Round {n} still has unfinished games. Enter every result before pairing the next round.',
                        { n: round.roundNo }
                    ));
                }
            });
        });

        const stats = window.TeamEngine.calculateTeamStats(teams, rounds, {
            pointSystem: meta.pointSystem,
            matchPointSystem: meta.matchPointSystem,
            boards: meta.boards
        });

        const teamNoOf = (id) => (stats[id] ? stats[id].teamNo : 0);
        const pts = this.pointValues(meta.matchPointSystem);

        // ---- cells -----------------------------------------------------------
        const cells = {};
        teams.forEach(t => {
            cells[t.id] = rounds.map(r => this.cellFor(stats[t.id], r.roundNo, teamNoOf));
        });

        // One extra column for the round about to be paired. 'Z' (zero-point bye) is how
        // the engine is told to leave a team out; an entered team gets nothing, which marks
        // it available.
        let excluded = 0;
        teams.forEach(t => {
            if (t.status === false) { cells[t.id].push({ oppNo: '0000', color: '-', result: 'Z' }); excluded++; }
            else cells[t.id].push(null);
        });
        if (teams.length - excluded < 2) {
            throw new Error(say('At least two teams must be entered to pair a round.'));
        }

        // ---- scores ----------------------------------------------------------
        // Derived from the cells rather than copied from the standings, because
        // bbpPairings validates that the declared score equals the score its own model
        // implies and refuses the whole file when they disagree. Any divergence from what
        // the arbiter sees is reported instead of being papered over.
        const scoreOf = {};
        teams.forEach(t => {
            scoreOf[t.id] = cells[t.id].reduce((sum, c) => {
                if (!c) return sum;
                if (c.result === '1') return sum + pts.win;
                if (c.result === '=') return sum + pts.draw;
                if (c.result === 'U') return sum + pts.pab;
                if (c.result === 'Z') return sum;
                return sum + pts.loss;
            }, 0);
            const shown = stats[t.id].matchPoints;
            if (Math.abs(shown - scoreOf[t.id]) > 1e-9) {
                warnings.push(
                    `${t.name} shows ${shown} match point(s) in the standings but pairs as ` +
                    `${scoreOf[t.id]}.`
                );
            }
        });

        // ---- ranking order ---------------------------------------------------
        // bbpPairings derives its ranking from the ORDER THE 001 LINES APPEAR, not from the
        // rank column (`tournament.playersByRank.push_back(id)` as each line is read, then
        // computePlayerIndexes overwrites the indexes when no explicit rank is in use). So
        // emitting the teams in C.04.6 art. 4.4 order — primary score, then secondary, then
        // TPN — is what makes the engine order each scoregroup the way the team rules
        // require, without disturbing the pairing numbers that art. 3.4 ties the bye to.
        const ordered = window.TeamEngine.orderTeams(teams.map(t => t.id), stats, 'match');
        const rankOf = {};
        ordered.forEach((id, i) => { rankOf[id] = i + 1; });
        const teamById = new Map(teams.map(t => [t.id, t]));

        // ---- document --------------------------------------------------------
        const lines = [];
        lines.push('012 ' + (meta.name || 'Team Tournament'));

        // Always declared: none of these match bbpPairings' defaults (see pointValues).
        const P = window.PairingTRF;
        lines.push(P.bbPointsLine('BBW', pts.win));
        lines.push(P.bbPointsLine('BBD', pts.draw));
        lines.push(P.bbPointsLine('BBL', pts.loss));
        lines.push(P.bbPointsLine('BBU', pts.pab));
        lines.push(P.bbPointsLine('BBZ', pts.zeroPointBye));
        lines.push(P.bbPointsLine('BBF', pts.forfeitLoss));

        ordered.forEach(id => {
            const t = teamById.get(id);
            lines.push(this.teamLine(t, stats[id], cells[id], scoreOf[id], rankOf[id]));
        });

        const colourToken = P.initialColourToken(meta.initialColour);
        if (colourToken) {
            lines.push('XXC ' + colourToken);
        } else if (rounds.length === 0) {
            throw new Error(say(
                'No initial colour is set for round 1. Choose whether team pairing number 1 starts with White or Black.'
            ));
        }
        lines.push('XXR ' + totalRounds);

        const teamByNo = new Map(teams.map(t => [parseInt(t.teamNo, 10), t.id]));
        return { trf: lines.join(NL) + NL, teamByNo, warnings };
    }
};

if (typeof module !== 'undefined') {
    module.exports = { TeamPairingTRF };
} else {
    window.TeamPairingTRF = TeamPairingTRF;
}
